import React, { useEffect, useState } from 'react'
import { Home, User, Briefcase, FileText, Mail, Moon, Sun, GraduationCap, Sparkles } from 'lucide-react'
import { useScrollSpy } from '../hooks/useScrollSpy'

const Sidebar = ({ darkMode, setDarkMode }) => {
  const activeSection = useScrollSpy(['home', 'about', 'education', 'projects', 'resume', 'contact'], 100)
  const [progress, setProgress] = useState(0)
  const [hoveredItem, setHoveredItem] = useState(null)

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement
      const total = el.scrollHeight - el.clientHeight
      const p = total > 0 ? el.scrollTop / total : 0
      setProgress(p)
    }
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navItems = [
    { name: 'Home', href: '#home', icon: Home, gradient: 'from-blue-500 to-cyan-400' },
    { name: 'About', href: '#about', icon: User, gradient: 'from-purple-500 to-pink-400' },
    { name: 'Education', href: '#education', icon: GraduationCap, gradient: 'from-green-500 to-emerald-400' },
    { name: 'Projects', href: '#projects', icon: Briefcase, gradient: 'from-orange-500 to-yellow-400' },
    { name: 'Resume', href: '#resume', icon: FileText, gradient: 'from-indigo-500 to-blue-400' },
    { name: 'Contact', href: '#contact', icon: Mail, gradient: 'from-teal-500 to-cyan-400' },
  ]

  return (
    <>
      {/* Desktop Sidebar - Left */}
      <aside
        id="app-sidebar"
        className="hidden sm:flex fixed left-0 top-0 h-screen w-20 transition-all duration-500 ease-out z-50 group"
      >
        {/* Glassmorphic Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/30 to-white/20 dark:from-github-surface/80 dark:via-github-surface/70 dark:to-github-bg/60 backdrop-blur-2xl" />
        
        {/* Animated Border Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 via-purple-500/20 to-cyan-500/20 dark:from-github-border/20 dark:via-github-border/20 dark:to-github-border/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute inset-y-0 right-0 w-[2px] bg-gradient-to-b from-transparent via-blue-500/50 dark:via-github-border to-transparent" />
        
        {/* Content Container */}
        <div className="relative flex flex-col items-center justify-between h-full py-8 w-full">
          {/* Logo */}
          <a 
            href="#home" 
            className="group/logo relative"
          >
            <div className="relative w-12 h-12 rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-300 hover:scale-110 hover:rotate-6">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 via-purple-500 to-cyan-400 dark:from-github-border dark:via-github-border dark:to-github-border animate-gradient-shift" />
              <div className="absolute inset-[2px] bg-white dark:bg-github-surface rounded-2xl flex items-center justify-center p-1">
                <img 
                  src={darkMode ? "/SCP_LOGO1.png" : "/SCP_LOGO.png"} 
                  alt="SCP Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <Sparkles className="absolute top-1 right-1 w-3 h-3 text-yellow-400 opacity-0 group-hover/logo:opacity-100 transition-opacity" />
            </div>
          </a>

          {/* Navigation */}
          <nav className="flex flex-col items-center gap-3 w-full px-4">
            {navItems.map((item, idx) => {
              const Icon = item.icon
              const isActive = activeSection === item.href.slice(1)
              return (
                <a
                  key={item.name}
                  href={item.href}
                  onMouseEnter={() => setHoveredItem(idx)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="group/item relative flex items-center justify-center w-12 h-12 transition-all duration-300"
                >
                  {/* Glow Effect */}
                  {isActive && (
                    <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${item.gradient} dark:from-github-border dark:to-github-border opacity-20 blur-xl animate-pulse`} />
                  )}
                  
                  {/* Main Button */}
                  <div className={
                    `relative w-full h-full rounded-xl flex items-center justify-center transition-all duration-300 ` +
                    (isActive 
                      ? `bg-gradient-to-br ${item.gradient} dark:from-github-border dark:to-github-border shadow-xl scale-110` 
                      : 'bg-white/50 dark:bg-github-surface/80 hover:bg-white/80 dark:hover:bg-github-surface hover:scale-110')
                  }>
                    <Icon 
                      size={20} 
                      strokeWidth={2.5} 
                      className={`transition-all duration-300 ${
                        isActive 
                          ? 'text-white' 
                          : 'text-slate-600 dark:text-github-text group-hover/item:text-blue-500 dark:group-hover/item:text-github-accent'
                      }`}
                    />
                    
                    {/* Active Indicator */}
                    {isActive && (
                      <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1.5 h-6 rounded-full bg-white shadow-lg" />
                    )}
                  </div>

                  {/* Tooltip */}
                  <span className={
                    `absolute left-full ml-4 px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap pointer-events-none transition-all duration-300 shadow-2xl ` +
                    `bg-gradient-to-r ${item.gradient} dark:from-github-border dark:to-github-border text-white ` +
                    (hoveredItem === idx ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2')
                  }>
                    {item.name}
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 rotate-45 bg-gradient-to-r ${item.gradient} dark:from-github-border dark:to-github-border`} />
                  </span>
                </a>
              )
            })}
          </nav>

          {/* Bottom Section */}
          <div className="flex flex-col items-center gap-4 w-full px-4">
            {/* Theme Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="group/theme relative w-12 h-12 rounded-xl bg-white/50 dark:bg-github-surface/80 hover:bg-white/80 dark:hover:bg-github-surface flex items-center justify-center transition-all duration-300 hover:scale-110 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 opacity-0 group-hover/theme:opacity-20 transition-opacity" />
              {darkMode ? (
                <Sun size={20} strokeWidth={2.5} className="text-yellow-500 transition-transform group-hover/theme:rotate-180 duration-500" />
              ) : (
                <Moon size={20} strokeWidth={2.5} className="text-slate-600 dark:text-github-muted transition-transform group-hover/theme:-rotate-12 duration-500" />
              )}
            </button>
            
            {/* Progress Bar */}
            <div className="relative w-1 h-24 bg-slate-200/30 dark:bg-github-border/50 rounded-full overflow-hidden">
              <div
                className="absolute bottom-0 w-full bg-gradient-to-t from-blue-500 via-purple-500 to-cyan-400 dark:from-github-border dark:via-github-border dark:to-github-border transition-all duration-300 rounded-full"
                style={{ height: `${progress * 100}%` }}
              />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar - Bottom */}
      <aside className="sm:hidden fixed bottom-0 left-0 right-0 z-50">
        {/* Glassmorphic Background */}
        <div className="relative bg-white/40 dark:bg-github-surface/90 backdrop-blur-2xl border-t border-white/20 dark:border-github-border shadow-2xl">
          {/* Top Border Gradient */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 dark:via-github-border to-transparent" />
          
          <div className="flex items-center justify-around px-2 py-3">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = activeSection === item.href.slice(1)
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className="group/mobile relative flex flex-col items-center gap-1 min-w-[60px] transition-all duration-300"
                >
                  {/* Glow */}
                  {isActive && (
                    <div className={`absolute -top-1 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-r ${item.gradient} opacity-20 blur-xl`} />
                  )}
                  
                  {/* Icon Container */}
                  <div className={
                    `relative w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-300 ` +
                    (isActive 
                      ? `bg-gradient-to-br ${item.gradient} shadow-lg scale-110 -translate-y-1` 
                      : 'bg-white/50 dark:bg-github-surface/80 group-hover/mobile:scale-105')
                  }>
                    <Icon 
                      size={18} 
                      strokeWidth={2.5} 
                      className={isActive ? 'text-white' : 'text-slate-600 dark:text-github-text'}
                    />
                    {isActive && (
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-white shadow-lg" />
                    )}
                  </div>
                  
                  {/* Label */}
                  <span className={
                    `text-[10px] font-semibold transition-all duration-300 ` +
                    (isActive 
                      ? 'text-blue-600 dark:text-github-accent scale-105' 
                      : 'text-slate-500 dark:text-github-muted')
                  }>
                    {item.name}
                  </span>
                </a>
              )
            })}
            
            {/* Theme Toggle Mobile */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="flex flex-col items-center gap-1 min-w-[60px]"
            >
              <div className="w-11 h-11 rounded-2xl bg-white/50 dark:bg-github-surface/80 flex items-center justify-center transition-all duration-300 hover:scale-105">
                {darkMode ? (
                  <Sun size={18} strokeWidth={2.5} className="text-yellow-500" />
                ) : (
                  <Moon size={18} strokeWidth={2.5} className="text-slate-600 dark:text-github-muted" />
                )}
              </div>
              <span className="text-[10px] font-semibold text-slate-500 dark:text-github-muted">Theme</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

export default Sidebar
